import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import siteText from '@/i18n/local/siteText';

type SiteLocale = 'zh-TW' | 'zh-CN' | 'en' | 'ja';
type TranslationMap = Record<string, string>;

const translatableAttributes = ['title', 'aria-label', 'placeholder', 'alt'] as const;

function normalizeLocale(value: string | undefined): SiteLocale {
  if (value?.startsWith('zh-CN')) return 'zh-CN';
  if (value?.startsWith('en')) return 'en';
  if (value?.startsWith('ja')) return 'ja';
  return 'zh-TW';
}

function withWhitespace(source: string, translated: string) {
  const leading = source.match(/^\s*/)?.[0] ?? '';
  const trailing = source.match(/\s*$/)?.[0] ?? '';
  return `${leading}${translated}${trailing}`;
}

function translateValue(source: string, dictionary: TranslationMap) {
  const trimmed = source.trim();
  if (!trimmed) return source;

  const direct = dictionary[trimmed];
  if (direct) return withWhitespace(source, direct);

  const matches = Object.keys(dictionary)
    .filter((key) => key.length > 2 && trimmed.includes(key))
    .sort((left, right) => right.length - left.length);
  if (matches.length === 0) return source;

  let translated = trimmed;
  for (const key of matches) translated = translated.replaceAll(key, dictionary[key]);
  return withWhitespace(source, translated);
}

export default function SiteLocaleTranslator() {
  const { i18n } = useTranslation();
  const locale = normalizeLocale(i18n.resolvedLanguage ?? i18n.language);
  const originalText = useRef(new WeakMap<Text, string>());
  const originalAttributes = useRef(new WeakMap<Element, Map<string, string>>());

  useEffect(() => {
    document.documentElement.lang = locale;

    const dictionary = locale === 'zh-TW' ? {} : (siteText[locale] ?? {});
    const translatePage = () => {
      if (!document.body) return;

      const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
      let node = walker.nextNode() as Text | null;
      while (node) {
        const parent = node.parentElement;
        if (parent && !parent.closest('script,style,noscript,textarea,select')) {
          const source = originalText.current.get(node) ?? node.nodeValue ?? '';
          originalText.current.set(node, source);
          const nextValue = translateValue(source, dictionary);
          if (node.nodeValue !== nextValue) node.nodeValue = nextValue;
        }
        node = walker.nextNode() as Text | null;
      }

      document.querySelectorAll<HTMLElement>('[title],[aria-label],[placeholder],[alt]').forEach((element) => {
        if (element.closest('script,style,noscript')) return;
        let attributes = originalAttributes.current.get(element);
        if (!attributes) {
          attributes = new Map<string, string>();
          originalAttributes.current.set(element, attributes);
        }

        for (const name of translatableAttributes) {
          const source = attributes.get(name) ?? element.getAttribute(name);
          if (source == null) continue;
          attributes.set(name, source);
          const nextValue = translateValue(source, dictionary);
          if (element.getAttribute(name) !== nextValue) element.setAttribute(name, nextValue);
        }
      });
    };

    const observer = new MutationObserver(() => {
      queueMicrotask(translatePage);
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    translatePage();

    return () => observer.disconnect();
  }, [locale]);

  return null;
}
